
import { defineComponent, PropType, ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { zh_tran } from '@/utils/lang.js'
import { getStockClass } from '@/utils/stock'
import Nodata from '@/components/page/nodata.vue'

export interface StrategyItem {
  code: string
  name: string
  market: string
  dayReturn: number
  day3Return: number
  day5Return: number
  month3Return: number
  yearReturn: number
  maxDrawDown: number
  holdPeriod: number
  tags: string[]
  profile: string
}

export default defineComponent({
  name: 'StrategyTable',
  components: { Nodata },
  props: {
    list: { type: Array as PropType<StrategyItem[]>, default: () => [] },
    selectedCode: { type: String, default: '' },
    isTraditional: { type: Boolean, default: false },
    isDark: { type: Boolean, default: true },
    prefer: { type: String, default: '' },
    unsubscribeLoading: { type: Boolean, default: false },
    loading: { type: Boolean, default: false }
  },
  emits: ['select', 'unsubscribe'],
  setup(props, { emit }) {
    // StrategyTable 局部主题变量
    const tableVars = computed(() => props.isDark ? ({
      panelBg:      '#111424',
      panelBorder:  'rgba(255,255,255,0.06)',
      headerBg:     '#111424',
      headerText:   '#ffffff',
      thColor:      '#7C7E87',
      textPrimary:  'rgba(255,255,255,0.85)',
      textSecondary:'rgba(255,255,255,0.45)',
      rowHover:     'rgba(255,255,255,0.04)',
      rowActive:    'rgba(74,158,255,0.08)',
    }) : ({
      panelBg:      '#FFFFFF',
      panelBorder:  '#e8e8e8',
      headerBg:     '#FFFFFF',
      headerText:   '#1d1d1d',
      thColor:      '#808080',
      textPrimary:  '#1d1d1d',
      textSecondary:'#7d7d7d',
      rowHover:     '#f5f7fa',
      rowActive:    'rgba(74,158,255,0.06)',
    }))
    const contextMenuVisible = ref(false)
    const contextMenuX = ref(0)
    const contextMenuY = ref(0)
    const contextMenuItem = ref<StrategyItem | null>(null)

    // 文字转换：繁体时调用 .tran()，简体直接返回
    function tran(text: string) {
      if (props.isTraditional) {
        return (text as any).tran()
      }
      return text
    }

    // 数据变化后重新触发 DOM 转换
    function switchLanguage() {
      if (props.isTraditional) {
        nextTick(() => zh_tran('t'))
      }
    }

    const formatPct = (val: number | undefined) => {
      if (val == null) return '--'
      const pct = val * 100
      const sign = pct > 0 ? '+' : ''
      return sign + pct.toFixed(2) + '%'
    }
    
    const formatPcts = (val: number | undefined) => {
      if (val == null) return '--'
      const pct = val * 100
      const sign = pct > 0 ? '' : ''
      return sign + pct.toFixed(2) + '%'
    }
    const pctClass = (val: number | undefined) => {
      return getStockClass(val ?? null, props.prefer || undefined)
    }

    const holdPeriodText = (val: number | undefined) => {
      if (val == null) return '--'
      return val === 0 ? tran('不定期') : val + tran('日')
    }

    const showContextMenu = (e: MouseEvent, item: StrategyItem) => {
      contextMenuX.value = e.clientX
      contextMenuY.value = e.clientY
      contextMenuItem.value = item
      contextMenuVisible.value = true
    }

    const handleUnsubscribe = () => {
      if (props.unsubscribeLoading) return
      if (contextMenuItem.value) {
        emit('unsubscribe', contextMenuItem.value)
      }
      contextMenuVisible.value = false
    }

    const hideContextMenu = () => {
      contextMenuVisible.value = false
    }

    // 列表数据变化后重新转换 DOM（接口数据渲染完成后）
    watch(() => props.list, () => switchLanguage(), { deep: false })

    onMounted(() => {
      document.addEventListener('click', hideContextMenu)
      switchLanguage()
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', hideContextMenu)
    })

    return { tran, tableVars, formatPct, formatPcts, pctClass, holdPeriodText, showContextMenu, handleUnsubscribe, contextMenuVisible, contextMenuX, contextMenuY }
  }
})